import React, { useEffect, useRef, useState } from "react";

const API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

let mapsPromise;

/** Loads the Maps JS API once per page, on first use. */
function loadMaps() {
  if (!mapsPromise) {
    mapsPromise = new Promise((resolve, reject) => {
      // `loading=async` without a callback logs a console warning, so use the documented one.
      window.__applaussMapsReady = resolve;
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${API_KEY}&loading=async&callback=__applaussMapsReady`;
      script.async = true;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }
  return mapsPromise;
}

const component = (place, type) =>
  place.addressComponents?.find((c) => c.types.includes(type))?.longText || "";

/**
 * Maps a selected Google Place to the venue fields the admin form edits.
 * Country/state are stored as full names and `address` as the street line only,
 * matching what the ingestion normalizers write. `placeId` comes from the prediction
 * rather than the fetched Place — it is available before fetchFields, so it costs nothing.
 */
export function placeToVenueFields(place, placeId) {
  const city = component(place, "locality") || component(place, "postal_town");
  const segments = (place.formattedAddress || "").split(",").map((s) => s.trim());
  // Street number and route are ordered differently per locale ("113, Carrer X" vs
  // "113 Main St"), so slice Google's own formatted address at the city instead of
  // reassembling the components: everything before the city segment is the street line.
  const cityAt = city ? segments.findIndex((s) => s.includes(city)) : -1;
  const address = (cityAt > 0 ? segments.slice(0, cityAt) : segments.slice(0, 1)).join(", ");

  return {
    google_place_id: placeId || "",
    address,
    city,
    state: component(place, "administrative_area_level_1"),
    country: component(place, "country"),
    latitude: place.location?.lat() ?? "",
    longitude: place.location?.lng() ?? "",
    ...(place.websiteURI ? { url: place.websiteURI } : {}),
  };
}

const PLACE_FIELDS = ["addressComponents", "formattedAddress", "location", "websiteURI"];

/**
 * Google Places search box that fills a venue's location fields, above a read-only
 * map pinned at the venue's current coordinates.
 */
export default function PlacePicker({ latitude, longitude, googlePlaceId, onSelect }) {
  const hostRef = useRef(null);
  const onSelectRef = useRef(onSelect);
  const [failed, setFailed] = useState(false);
  onSelectRef.current = onSelect;

  useEffect(() => {
    if (!API_KEY) return undefined;
    let element;
    let cancelled = false;

    loadMaps()
      .then(() => window.google.maps.importLibrary("places"))
      .then(({ PlaceAutocompleteElement }) => {
        if (cancelled || !hostRef.current) return;
        element = new PlaceAutocompleteElement();
        element.addEventListener("gmp-select", async ({ placePrediction }) => {
          try {
            const place = placePrediction.toPlace();
            await place.fetchFields({ fields: PLACE_FIELDS });
            onSelectRef.current(placeToVenueFields(place, placePrediction.placeId));
          } catch (err) {
            console.error("Error fetching place details:", err);
            setFailed(true);
          }
        });
        hostRef.current.appendChild(element);
      })
      .catch(() => !cancelled && setFailed(true));

    return () => {
      cancelled = true;
      element?.remove();
    };
  }, []);

  const hasPin = latitude !== "" && longitude !== "" && latitude != null && longitude != null;

  if (!API_KEY) {
    return (
      <p className="text-xs text-muted-foreground">
        Set <code>VITE_GOOGLE_MAPS_API_KEY</code> to look this venue up on Google Maps.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">Find on Google Maps</label>
      <div ref={hostRef} />
      {failed && (
        <p className="text-xs text-destructive">
          Google Maps lookup is unavailable — edit the fields below manually.
        </p>
      )}
      {googlePlaceId && (
        <p className="text-xs text-muted-foreground">
          Linked to Google Maps ·{" "}
          <button
            type="button"
            onClick={() => onSelectRef.current({ google_place_id: "" })}
            className="underline hover:text-foreground"
          >
            Clear
          </button>
        </p>
      )}
      {hasPin && (
        <iframe
          title="Venue location"
          className="h-48 w-full rounded-md border"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          src={`https://www.google.com/maps/embed/v1/place?key=${API_KEY}&q=${latitude},${longitude}&zoom=16`}
        />
      )}
    </div>
  );
}
